import { queryOptions, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';

import { EmptyList } from '@components/EmptyList';
import { Pagination } from '@components/ui/pagination';
import { queryKeys } from '@constants/queryKeys.constant';
import { EStaleTime } from '@enums/staleTime.enum';
import { formatFullName } from '@formatters/fullName.formatter';
import {
  getAllInterventionsSchema,
  type GetAllInterventionsParams,
} from '@services/interventions/dtos/getAll.dto';
import { interventionService } from '@services/interventions/service';

import { InterventionFilters } from './@components/Filters';
import { InterventionTableCell } from './@components/TableCell';

function interventionsQueryOptions(params: GetAllInterventionsParams) {
  return queryOptions({
    staleTime: EStaleTime.OneHour,
    queryKey: queryKeys.interventions.list(params),
    queryFn: () =>
      interventionService.getAll({
        page: params.page,
        status: params.status,
      }),
  });
}

export const Route = createFileRoute('/_layout/(private)/interventions/')({
  component: Interventions,
  validateSearch: search => getAllInterventionsSchema.parse(search),
  loaderDeps: ({ search }) => ({ page: search.page, status: search.status }),
  loader: ({ context, deps }) => {
    const options = interventionsQueryOptions(deps);
    return context.queryClient.ensureQueryData({
      ...options,
      revalidateIfStale: true,
    });
  },
});

function Interventions() {
  const { page, status } = Route.useSearch();
  const { data } = useSuspenseQuery(
    interventionsQueryOptions({ page, status }),
  );

  return (
    <div className="relative">
      <InterventionFilters />
      <div
        className="bg-background mb-2 grid w-full overflow-hidden rounded-xl border border-zinc-200 shadow"
        role="table"
      >
        <div
          className="bg-primary-light-two dark:text-primary-dark-one grid h-12 grid-cols-7 items-center font-medium text-zinc-600"
          role="row"
        >
          {headers.map(header => (
            <span
              className="text-center text-sm font-medium"
              key={header.accessorKey}
              role="columnheader"
            >
              {header.title}
            </span>
          ))}
        </div>

        {data.list.map(row => (
          <Link
            className="even:bg-row-even bg-row-odd hover:bg-row-odd-hover even:hover:bg-row-even-hover grid min-h-14 grid-cols-7 items-center justify-between transition-all duration-200"
            key={row.id}
            params={{ id: row.serviceRequestId }}
            role="row"
            to="/service-requests/$id"
          >
            <InterventionTableCell type="id" value={row.displayId} />
            <InterventionTableCell type="date" value={row.createdAt} />

            <InterventionTableCell value={row.service.translations.en} />
            <InterventionTableCell
              value={row.provider?.companyName || 'Unassigned'}
            />
            <InterventionTableCell
              value={row.requester.language.toUpperCase()}
            />
            <InterventionTableCell
              value={formatFullName({
                firstName: row.requester.firstName,
                lastName: row.requester.lastName,
              })}
            />
            <InterventionTableCell type="tag" value={row.status} />
          </Link>
        ))}

        <EmptyList
          alt="No interventions animation/image"
          description="Go to a service request and create an intervention to see it here"
          isVisible={!data.list.length}
          title="No interventions yet."
        />
      </div>

      <div className="ml-auto flex w-fit items-center">
        <span className="mr-4 text-sm text-gray-500 dark:text-gray-300">
          Total: {data.count || 0} intervention(s)
        </span>
        <Pagination
          currentPage={data.pagination.page}
          totalPages={data.pagination.totalPages}
        />
      </div>
    </div>
  );
}

const headers = [
  { title: 'ID', accessorKey: 'displayId' },
  { title: 'Creation date', accessorKey: 'createdAt' },
  { title: 'Service', accessorKey: 'service.translations.en' },
  { title: 'Provider', accessorKey: 'provider.companyName' },
  { title: 'SR Language', accessorKey: 'requester.language' },
  { title: 'Requester', accessorKey: 'requesterFullName' },
  { title: 'Status', accessorKey: 'status' },
];
